import {Modal} from 'bootstrap';
import mitt, {Emitter} from 'mitt';

// Typs for confirmation modal events
type ConfirmationModalEvent = {
    confirm: any,
    decline: any,
    cancel: any
};

/**
 * Wrapper for a Bootstrap modal, that implements a yes/no confirmation dialog with according events. Each modal-button should have the attribute
 * `data-bs-dismiss="modal"`, so that the modal is closed at all times on click. The buttons also need to have an attribute called `data-confirmation`, which
 * must either have the value "confirm" for the confirm button, "decline" for the decline button or "cancel" for any cancel buttons.
 *
 * @author Philipp Seiler <ps@typoheads.at>
 */
export default class ConfirmationModal {
    private _emitter: Emitter<ConfirmationModalEvent> = mitt<ConfirmationModalEvent>();

    /**
     * Modal container.
     */
    private readonly _container: HTMLElement;

    /**
     * Bootstrap modal instance.
     */
    private readonly _modal: Modal;

    /**
     * Some value associated to the confirmation dialog. May be used to e.g. initiate further actions, once the confirm-button is clicked.
     */
    private _value: any = null;

    /**
     * Button for confirming the modal.
     */
    private confirmButton: HTMLElement;

    /**
     * Button for declining the modal.
     */
    private declineButton: HTMLElement;

    /**
     * Buttons for cancelling the modal.
     */
    private cancelButtons: NodeListOf<HTMLElement>;

    constructor(container: HTMLElement) {
        this._container = container;
        this._modal = Modal.getOrCreateInstance(container);
        const confirmButton = container.querySelector<HTMLElement>('[data-confirmation="confirm"]');
        const declineButton = container.querySelector<HTMLElement>('[data-confirmation="decline"]');
        const cancelButtons = container.querySelectorAll<HTMLElement>('[data-confirmation="cancel"]');
        if (!confirmButton || !declineButton || !cancelButtons) {
            throw new Error('Required elements missing.');
        }

        this.confirmButton = confirmButton;
        this.declineButton = declineButton;
        this.cancelButtons = cancelButtons;

        // Setup components
        this.initializeConfirmButton();
        this.initializeDeclineButton();
        this.initializeCancelButtons();
    }

    get emitter(): Emitter<ConfirmationModalEvent> {
        return this._emitter;
    }

    get container(): HTMLElement {
        return this._container;
    }

    get modal(): Modal {
        return this._modal;
    }

    /**
     * Gets the value associated to the confirmation dialog. May be used to e.g. initiate further actions, once the confirm-button is clicked.
     *
     * @returns {any}
     */
    get value(): any {
        return this._value;
    }

    /**
     * Sets the value associated to the confirmation dialog.
     *
     * @param {any} value new value to set
     * @returns {any}
     */
    set value(value: any) {
        this._value = value;
    }

    /**
     * Resets the value associated to the confirmation dialog.
     *
     * @returns {any}
     */
    public resetValue() {
        this.value = null;
    }

    /**
     * Initializes the confirm button.
     *
     * @private
     */
    private initializeConfirmButton(): void {
        this.confirmButton.addEventListener('click', () => {
            this.emitter.emit('confirm', this.value);
            this.resetValue();
        });
    }

    /**
     * Initializes the decline button.
     *
     * @private
     */
    private initializeDeclineButton(): void {
        this.declineButton.addEventListener('click', () => {
            this.emitter.emit('decline', this.value);
            this.resetValue();
        });
    }

    /**
     * Initializes the cancel buttons.
     *
     * @private
     */
    private initializeCancelButtons(): void {
        this.cancelButtons.forEach((button: HTMLElement) => {
            button.addEventListener('click', () => {
                this.emitter.emit('cancel', this.value);
                this.resetValue();
            });
        });
    }

    /**
     * Shortcut to show the Bootstrap Modal.
     *
     * @private
     */
    public show(): void {
        this.modal.show();
    }

    /**
     * Shortcut to hide the Bootstrap Modal.
     *
     * @private
     */
    public hide(): void {
        this.modal.hide();
    }

    /**
     * Shortcut to toggle the Bootstrap Modal.
     *
     * @private
     */
    public toggle(): void {
        this.modal.toggle();
    }
}
